import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Card, Input } from '@/shared/ui';
import { useSettings } from '@/shared/hooks/useSettings';
import { useReplicateClient } from '@/shared/hooks/useReplicateClient';
import { LLM_MODELS } from '@/shared/catalog/models';
import { newId } from '@/shared/lib/id';
import { getDB } from '@/shared/storage';
import { createJobs, planResearchOnly } from '@/features/image-generation/plan';
import { EMPTY_DNA } from './BrandDnaForm';

export function NewBrandPage() {
  const [settings] = useSettings();
  const client = useReplicateClient();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [llmModelId, setLlmModelId] = useState(settings.defaultLlmModel);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function createBrand(): Promise<string> {
    const brandId = newId('brand');
    const now = Date.now();
    await getDB().brands.add({
      id: brandId,
      name: name.trim(),
      ...(url.trim() ? { url: url.trim() } : {}),
      dna: EMPTY_DNA,
      createdAt: now,
      updatedAt: now,
    });
    return brandId;
  }

  async function researchFromUrl() {
    if (!client) {
      setError('Set your Replicate token in Settings first.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const brandId = await createBrand();
      const specs = planResearchOnly({
        brandName: name.trim(),
        brandUrl: url.trim(),
        scrapedSnippet: '',
        llmModelId,
        brandId,
        applyToBrand: true,
      });
      await createJobs(specs);
      void navigate({ to: '/brands/$brandId', params: { brandId } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create brand.');
      setBusy(false);
    }
  }

  async function skipManual() {
    setError(null);
    setBusy(true);
    try {
      const brandId = await createBrand();
      void navigate({ to: '/brands/$brandId', params: { brandId } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create brand.');
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <h1 className="text-2xl font-semibold">New brand</h1>
      <p className="text-sm text-muted-foreground">
        Provide a URL to auto-research the brand DNA in the background, or skip and fill it in by hand.
      </p>
      <Card className="space-y-4">
        <Field label="Brand name *">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme"
          />
        </Field>
        <Field label="Brand URL (optional)">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://acme.com"
          />
        </Field>
        <Field label="LLM for research">
          <select
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
            value={llmModelId}
            onChange={(e) => setLlmModelId(e.target.value)}
          >
            {LLM_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => void researchFromUrl()}
            disabled={!name.trim() || !url.trim() || busy || !client}
          >
            Research from URL
          </Button>
          <Button
            variant="secondary"
            onClick={() => void skipManual()}
            disabled={!name.trim() || busy}
          >
            Skip, fill manually
          </Button>
        </div>
        {!client ? (
          <p className="text-xs text-amber-300">
            Set your Replicate token in Settings before researching.
          </p>
        ) : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
